import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, Button, Badge } from '@components';
import { SMSTemplate } from '../types';

interface SMSTemplatesTableProps {
  templates: SMSTemplate[];
  onEditTemplate: (id: string) => void;
  onDeleteTemplate: (id: string) => void;
  onCreateTemplate?: () => void;
}

export const SMSTemplatesTable: React.FC<SMSTemplatesTableProps> = ({
  templates,
  onEditTemplate,
  onDeleteTemplate,
  onCreateTemplate,
}) => {
  const { t } = useTranslation();
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('communications.smsTemplates')}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">
                  {t('communications.templateName')}
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">
                  {t('communications.messagePreview')}
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">
                  {t('common.status')}
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">
                  {t('communications.lastModified')}
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">
                  {t('communications.usageCount')}
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">
                  {t('common.actions')}
                </th>
              </tr>
            </thead>
            <tbody>
              {templates.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12">
                    <div className="text-center space-y-4">
                      <div className="mx-auto w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center">
                        <span className="material-icons text-2xl text-gray-400 dark:text-gray-500">
                          sms
                        </span>
                      </div>
                      <div className="space-y-2">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                          {t('communications.noSmsTemplatesFound')}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm mx-auto">
                          {t('communications.noSmsTemplatesFoundDescription')}
                        </p>
                      </div>
                      <div className="pt-2">
                        <Button
                          variant="gradient-multi"
                          size="sm"
                          onClick={onCreateTemplate}
                          className="inline-flex items-center gap-2"
                        >
                          <span className="material-icons text-sm">add</span>
                          {t('communications.createSmsTemplate')}
                        </Button>
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                templates.map(template => (
                  <tr key={template.id} className="border-b border-gray-100 dark:border-gray-800">
                    <td className="py-3 px-4 text-gray-900 dark:text-white font-medium">
                      {template.title}
                    </td>
                    <td className="py-3 px-4 text-gray-600 dark:text-gray-400 max-w-xs truncate">
                      {template.content}
                    </td>
                    <td className="py-3 px-4">
                      <Badge
                        variant={template.status === 'active' ? 'success' : 'warning'}
                        size="sm"
                      >
                        {template.status
                          .split(' ')
                          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                          .join(' ')}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 text-gray-600 dark:text-gray-400">
                      {new Date(template.last_modified).toLocaleDateString()}
                    </td>
                    <td className="py-3 px-4 text-gray-900 dark:text-white">
                      {template.usage_count}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onEditTemplate(template.id)}
                          className="p-1 h-8 w-8"
                          title="Edit Template"
                        >
                          <span className="material-icons text-lg">edit</span>
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onDeleteTemplate(template.id)}
                          className="p-1 h-8 w-8"
                          title="Delete Template"
                        >
                          <span className="material-icons text-lg text-red-600">delete</span>
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};
